import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
/* global BigInt */
import {
  deriveInitialImportExperience,
  getCanonicalReviewAvailability,
  suppressAiImportState,
} from '../viewmodels/parity/initialImportExperience';
import { useOptionalAiConsent } from './AiConsentContext';
import {
  INITIAL_CANONICAL_BANK_STATUS as INITIAL_BANK_STATUS,
  normalizeCanonicalBankStatus,
} from '../viewmodels/parity/canonicalBankStatus';
import { normalizeFrontendImportState } from '../viewmodels/parity/frontendImportState';
import {
  resolveInitialImportPollInterval,
  shouldContinueInitialImportPolling,
} from '../utils/initialImportPollingState';
import { recordImportBreadcrumb } from '../utils/instrumentation';
const InitialImportStatusContext = createContext(null);
const FALLBACK_BANK_STATUS = {};
const FALLBACK_EXPERIENCE = deriveInitialImportExperience(FALLBACK_BANK_STATUS);
const FALLBACK_IMPORT_STATE = normalizeFrontendImportState(FALLBACK_BANK_STATUS);
const fallbackApplyInitialImportStatusSnapshot = () => INITIAL_BANK_STATUS;
const fallbackRefreshInitialImportStatus = async () => INITIAL_BANK_STATUS;
const FALLBACK_CONTEXT_VALUE = {
  bankStatus: FALLBACK_BANK_STATUS,
  experience: FALLBACK_EXPERIENCE,
  importState: FALLBACK_IMPORT_STATE,
  isInitialImportStatusProviderActive: false,
  applyInitialImportStatusSnapshot: fallbackApplyInitialImportStatusSnapshot,
  refreshInitialImportStatus: fallbackRefreshInitialImportStatus,
};

export const InitialImportStatusProvider = ({ children }) => {
  const { household, authenticatedFetch } = useAuth();
  const aiConsent = useOptionalAiConsent();
  // Context absence must fail closed: no harness, cached subtree, or partial
  // provider mount may render or wait on an AI-only phase without proof of a
  // current grant and globally available processing.
  const aiCategorizationEnabled = Boolean(
    aiConsent?.granted && aiConsent?.state?.processingEnabled
  );
  const householdId = household?.id;
  const [storedBankStatus, setBankStatus] = useState(INITIAL_BANK_STATUS);
  const rawBankStatusRef = useRef(INITIAL_BANK_STATUS);
  const bankStatusRef = useRef(INITIAL_BANK_STATUS);
  const aiCategorizationEnabledRef = useRef(aiCategorizationEnabled);
  aiCategorizationEnabledRef.current = aiCategorizationEnabled;
  const consentStatusRef = useRef(aiConsent?.state?.status);
  consentStatusRef.current = aiConsent?.state?.status;
  const householdIdRef = useRef(householdId);
  // Child effects can start requests before our reset effect runs on a scope change.
  const bankStatus = householdIdRef.current === householdId ? storedBankStatus : INITIAL_BANK_STATUS;
  const isMountedRef = useRef(false);
  const refreshPromiseRef = useRef(null);

  const publishBankStatus = useCallback((nextStatus) => {
    const consentAwareStatus = aiCategorizationEnabledRef.current
      ? { ...nextStatus, aiCategorizationEnabled: true }
      : suppressAiImportState(nextStatus);
    const normalizedStatus = normalizeCanonicalBankStatus(consentAwareStatus);
    if (consentStatusRef.current && consentStatusRef.current !== 'missing') {
      normalizedStatus.aiCategorizationPromptReady = false;
    }
    bankStatusRef.current = normalizedStatus;
    if (isMountedRef.current) {
      setBankStatus(normalizedStatus);
    }
    return normalizedStatus;
  }, []);

  const commitBankStatus = useCallback((nextStatus) => {
    const currentRevision = rawBankStatusRef.current?.onboardingImportStatus?.workflowRevision;
    const incomingRevision = nextStatus?.onboardingImportStatus?.workflowRevision;
    const hasRevision = (value) => /^\d+$/.test(String(value ?? ''));
    if (hasRevision(currentRevision) && (!hasRevision(incomingRevision)
      || BigInt(incomingRevision) < BigInt(currentRevision))) return bankStatusRef.current;
    const newerRevision = hasRevision(incomingRevision) && (!hasRevision(currentRevision)
      || BigInt(incomingRevision) > BigInt(currentRevision));
    const incomingTime = Date.parse(nextStatus?.onboardingImportStatus?.snapshotObservedAt);
    const currentTime = Date.parse(rawBankStatusRef.current?.onboardingImportStatus?.snapshotObservedAt);
    if (!newerRevision && Number.isFinite(currentTime) && (!Number.isFinite(incomingTime) || incomingTime < currentTime)) {
      return bankStatusRef.current;
    }
    const normalizedRawStatus = normalizeCanonicalBankStatus(nextStatus);
    rawBankStatusRef.current = normalizedRawStatus;
    return publishBankStatus(normalizedRawStatus);
  }, [publishBankStatus]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      refreshPromiseRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (householdIdRef.current === householdId) return;
    householdIdRef.current = householdId;
    refreshPromiseRef.current = null;
    rawBankStatusRef.current = INITIAL_BANK_STATUS;
    commitBankStatus(INITIAL_BANK_STATUS);
  }, [commitBankStatus, householdId]);

  useEffect(() => {
    publishBankStatus(rawBankStatusRef.current);
  }, [aiCategorizationEnabled, aiConsent?.state?.status, publishBankStatus]);

  const refreshInitialImportStatus = useCallback(async ({ force = false } = {}) => {
    if (!householdId || typeof authenticatedFetch !== 'function') {
      commitBankStatus(INITIAL_BANK_STATUS);
      return INITIAL_BANK_STATUS;
    }

    if (refreshPromiseRef.current && !force) {
      return refreshPromiseRef.current;
    }

    const requestHouseholdId = householdId;
    let refreshPromise;
    refreshPromise = (async () => {
      try {
        recordImportBreadcrumb('status_refresh_start');
        const response = await authenticatedFetch('/bank/status');
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          recordImportBreadcrumb('status_refresh_failed');
          return null;
        }

        const nextStatus = normalizeCanonicalBankStatus(data);
        if (householdIdRef.current !== requestHouseholdId || refreshPromiseRef.current !== refreshPromise) {
          return bankStatusRef.current;
        }

        if (refreshPromiseRef.current === refreshPromise) {
          refreshPromiseRef.current = null;
        }

        commitBankStatus(nextStatus);
        recordImportBreadcrumb('status_refresh_success', {
          phase: nextStatus?.onboardingImportStatus?.phase,
          aiPhase: nextStatus?.onboardingImportStatus?.aiPhase,
          canEnterDashboard: nextStatus?.onboardingImportStatus?.canEnterDashboard,
          reviewReady: nextStatus?.onboardingImportStatus?.reviewReady,
          historicalImportComplete: nextStatus?.onboardingImportStatus?.historicalImportComplete,
          failed: nextStatus?.onboardingImportStatus?.failed,
          retryAvailable: nextStatus?.onboardingImportStatus?.retryAvailable,
        });
        return nextStatus;
      } catch (error) {
        recordImportBreadcrumb('status_refresh_failed');
        return null;
      } finally {
        if (refreshPromiseRef.current === refreshPromise) {
          refreshPromiseRef.current = null;
        }
      }
    })();

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  }, [authenticatedFetch, commitBankStatus, householdId]);

  const applyInitialImportStatusSnapshot = useCallback((snapshot = {}, options = {}) => {
    const snapshotImportStatus = snapshot?.onboardingImportStatus || snapshot?.importStatus;
    const hasImportStatus = snapshotImportStatus && typeof snapshotImportStatus === 'object';
    const hasTopLevelImportData = Boolean(
      snapshot?.onboardingImportState
        || snapshot?.onboardingImportStartedAt
        || snapshot?.onboardingImportCompletedAt
        || snapshot?.historicalImportComplete != null
        || snapshot?.budgetAutoBuildReady != null
        || snapshot?.linkedAccountsCount != null
        || snapshot?.connected != null
        || snapshot?.lastSyncAt != null
    );

    if (!hasImportStatus && !hasTopLevelImportData) {
      return bankStatusRef.current;
    }

    const currentStatus = rawBankStatusRef.current || INITIAL_BANK_STATUS;
    if (currentStatus.onboardingImportStatus?.snapshotObservedAt && !snapshotImportStatus?.snapshotObservedAt) {
      return bankStatusRef.current;
    }
    const {
      importStatus,
      onboardingImportStatus,
      ...rawTopLevelSnapshot
    } = snapshot;
    const topLevelSnapshot = Object.fromEntries(
      Object.entries(rawTopLevelSnapshot).filter(([, value]) => value !== undefined)
    );
    const nextRawStatus = {
      ...currentStatus,
      ...topLevelSnapshot,
      onboardingImportState: topLevelSnapshot.onboardingImportState
        || snapshotImportStatus?.phase
        || currentStatus.onboardingImportState,
      onboardingImportStatus: hasImportStatus
        ? {
            ...(currentStatus.onboardingImportStatus || {}),
            ...snapshotImportStatus,
          }
        : currentStatus.onboardingImportStatus,
    };

    const nextStatus = commitBankStatus(nextRawStatus);
    recordImportBreadcrumb('status_snapshot_applied', {
      source: options.source || 'unknown',
      phase: nextStatus?.onboardingImportStatus?.phase,
      aiPhase: nextStatus?.onboardingImportStatus?.aiPhase,
      canEnterDashboard: nextStatus?.onboardingImportStatus?.canEnterDashboard,
      reviewReady: nextStatus?.onboardingImportStatus?.reviewReady,
      historicalImportComplete: nextStatus?.onboardingImportStatus?.historicalImportComplete,
      failed: nextStatus?.onboardingImportStatus?.failed,
      retryAvailable: nextStatus?.onboardingImportStatus?.retryAvailable,
    });
    return nextStatus;
  }, [commitBankStatus]);

  useEffect(() => {
    if (!aiConsent?.state?.onboardingImportStatus) return;
    applyInitialImportStatusSnapshot({
      onboardingImportStatus: aiConsent.state.onboardingImportStatus,
      aiCategorizationEnabled,
      aiCategorizationPromptReady: false,
    }, { source: 'consent_decision' });
  }, [aiCategorizationEnabled, aiConsent?.state?.onboardingImportStatus, applyInitialImportStatusSnapshot]);

  useEffect(() => {
    void refreshInitialImportStatus();
  }, [refreshInitialImportStatus]);

  const experience = useMemo(
    () => deriveInitialImportExperience(bankStatus, { aiCategorizationEnabled }),
    [aiCategorizationEnabled, bankStatus]
  );
  const importState = useMemo(
    () => normalizeFrontendImportState(bankStatus),
    [bankStatus]
  );
  const reviewAvailability = useMemo(
    () => getCanonicalReviewAvailability(bankStatus, { aiCategorizationEnabled }),
    [aiCategorizationEnabled, bankStatus]
  );
  const shouldContinuePolling = shouldContinueInitialImportPolling({
    initialImportExperienceActive: experience.initialImportExperienceActive,
    onboardingImportStartedAt: bankStatus?.onboardingImportStartedAt,
    canEnterDashboard: reviewAvailability.canEnterDashboard,
    reviewReady: reviewAvailability.ready,
    failed: bankStatus?.onboardingImportStatus?.failed,
  });

  useEffect(() => {
    if (!householdId || typeof authenticatedFetch !== 'function') {
      return undefined;
    }

    const pollIntervalMs = resolveInitialImportPollInterval({ shouldContinuePolling });

    const interval = window.setInterval(() => {
      void refreshInitialImportStatus();
    }, pollIntervalMs);

    return () => window.clearInterval(interval);
  }, [authenticatedFetch, householdId, refreshInitialImportStatus, shouldContinuePolling]);

  const value = useMemo(() => ({
    bankStatus,
    experience,
    importState,
    isInitialImportStatusProviderActive: true,
    applyInitialImportStatusSnapshot,
    refreshInitialImportStatus,
  }), [applyInitialImportStatusSnapshot, bankStatus, experience, importState, refreshInitialImportStatus]);

  return (
    <InitialImportStatusContext.Provider value={value}>
      {children}
    </InitialImportStatusContext.Provider>
  );
};

export const useInitialImportStatus = () => {
  const context = useContext(InitialImportStatusContext);
  if (!context) {
    return FALLBACK_CONTEXT_VALUE;
  }
  return context;
};
